/* Render-time re-validation of a REMOTE trade glyph body — the `icon` a trade
 * may carry on `GET /categories` — before TradeIcon hands it to React's
 * `dangerouslySetInnerHTML`.
 *
 * This deliberately DUPLICATES admincli/commands/trades.go's validateIconBody
 * rather than trusting it. That CLI is not the only writer and cannot become
 * one: the Firebase console, a future admin UI, a leaked service-account key
 * and a restored backup all reach config/trades without passing through it.
 * Firestore content is a LOWER TRUST TIER THAN CODE, so the last check has to
 * stand here, on the render path, where the string actually becomes markup.
 *
 * REFUSES, never sanitises — a body that trips a rule falls back to the
 * toolbox. Rewriting an operator's paste (or an attacker's) would produce a
 * glyph nobody reviewed, rendered under an operator's name.
 *
 * Deliberately string-only: NO DOMParser. Both the deny-list and the element
 * scan run on the RAW text, because a parser skips over comments and CDATA and
 * Go's XML parser and a browser's HTML parser do not agree about what lives
 * inside them. Inspecting a parsed tree is exactly the check that
 * `<!-- <script src=…> -->` walks past.
 *
 * ONE rule of the Go side is not mirrored: it also parses for well-formedness.
 * Unbalanced markup is left to the browser's parser here, which auto-closes it
 * — every element it could then produce is already allowlisted and inert.
 */

/** trades.go's iconMaxBytes. Counted in BYTES, not UTF-16 units, so the two
 *  sides agree about a non-ASCII body. */
export const GLYPH_MAX_BYTES = 4096;

/** trades.go's iconDenied, matched case-insensitively against the raw text. */
const DENIED = ["<script", "<foreignobject", "javascript:"];

/** trades.go's iconEventAttr: `onclick=`, `ONLOAD =`, `onload\t=`. The
 *  whitespace tolerance is the whole point — either side of the `=` is exactly
 *  where a hand-written filter is usually got around. */
const EVENT_ATTR = /\bon[a-z]+\s*=/i;

/** trades.go's iconAllowedElements: shape primitives plus <g>. Everything that
 *  can fetch, script, embed or style (script, foreignObject, image, use, style,
 *  animate, a…) is absent, and absence is refusal. */
const ALLOWED = new Set(["path", "rect", "circle", "ellipse", "line", "polyline", "polygon", "g"]);

/** The tag opening at a `<`: whether it closes, and its name. Anything else
 *  after a `<` — `<!--`, `<!DOCTYPE`, `<?xml`, a bare `<` inside an attribute —
 *  does not match, and is refused. */
const TAG = /^(\/?)([a-zA-Z][a-zA-Z0-9-]*)/;

/** True if `body` is safe to render as the inner elements of an <svg>. */
export function isSafeGlyphBody(body: string): boolean {
  if (body.trim() === "") return false;
  if (new TextEncoder().encode(body).length > GLYPH_MAX_BYTES) return false;

  const lower = body.toLowerCase();
  if (DENIED.some((denied) => lower.includes(denied))) return false;
  if (EVENT_ATTR.test(body)) return false;

  // EVERY tag at EVERY depth, not just the top level: <g> is allowed to nest,
  // and a top-level-only check waves `<g><image href="…"/></g>` straight
  // through the one rule that is supposed to be exhaustive.
  let shapes = 0;
  for (let at = body.indexOf("<"); at !== -1; at = body.indexOf("<", at + 1)) {
    const tag = TAG.exec(body.slice(at + 1));
    if (!tag) return false;
    if (!ALLOWED.has(tag[2]!.toLowerCase())) return false;
    if (tag[1] === "") shapes += 1;
  }

  // A body of pure text carries no glyph; trades.go refuses it the same way.
  return shapes > 0;
}
